import { Ediabas } from "@emdzej/ediabasx-ediabas";
export class EmbeddedEdiabas {
    sgbdPath;
    iface;
    timeout;
    loadSgbdResolver;
    ediabas = null;
    /**
     * Last SGBD path loaded into the shared `Ediabas`. `job()` skips
     * a redundant `loadSgbd` when the same ECU is targeted again —
     * without this, every job would reset INITIALISIERUNG / IDENT /
     * systemResults inside `Ediabas` and re-probe the ECU.
     */
    loadedSgbdPath = null;
    lastResults = [];
    lastError = { code: 0, text: "" };
    currentState = "ready";
    /**
     * Single-lane serialiser around the shared `Ediabas`. JS is single-
     * threaded but `executeJob` / `loadSgbd` are async — interleaving
     * two `job()` calls would race on loaded-SGBD / INITIALISIERUNG /
     * parameter-set state and corrupt results. Mirrors the queue in
     * `EdiabasServer`.
     */
    queue = Promise.resolve();
    constructor(options) {
        this.sgbdPath = options.sgbdPath;
        this.iface = options.interface;
        this.timeout = options.timeout;
        this.loadSgbdResolver = options.loadSgbdResolver;
    }
    /**
     * Lazily create the inner `Ediabas` once per `EmbeddedEdiabas`
     * instance, mirroring the original single-instance contract.
     * Re-calling `init()` is idempotent — it just ensures the
     * transport is connected; it never replaces the inner `Ediabas`
     * (doing so would drop loaded-SGBD / INITIALISIERUNG / group-cache
     * state and leak the previous transport).
     */
    async init() {
        return this.enqueue(async () => {
            if (!this.ediabas) {
                this.ediabas = new Ediabas({
                    ecuPath: this.sgbdPath,
                    interface: this.iface,
                    timeout: this.timeout,
                    loadSgbdResolver: this.loadSgbdResolver,
                });
            }
            if (!this.ediabas.isConnected()) {
                await this.ediabas.connect();
            }
            this.lastResults = [];
            this.lastError = { code: 0, text: "" };
            this.currentState = "ready";
        });
    }
    async end() {
        return this.enqueue(async () => {
            if (this.ediabas) {
                await this.ediabas.disconnect();
                this.ediabas = null;
                this.loadedSgbdPath = null;
            }
            this.lastResults = [];
            this.lastError = { code: 0, text: "" };
            this.currentState = "ready";
        });
    }
    async job(ecu, jobName, params) {
        return this.enqueue(async () => {
            if (!this.ediabas)
                throw new Error("Not initialised — call init() first");
            this.currentState = "busy";
            this.lastError = { code: 0, text: "" };
            try {
                /* Cache key + filename are the same — `Ediabas.loadSgbd`
                   handles path resolution (Node `node:fs` lookup with
                   `.prg ↔ .grp` extension swap + case-insensitive directory
                   scan) and browser-side resolver dispatch internally.
                   We just track which name the inner `Ediabas` was loaded
                   with to skip the reload (which would otherwise reset
                   `initialized` / `identRan` / `systemResults` and force a
                   re-probe). */
                const cacheKey = ecu.toLowerCase();
                if (cacheKey !== this.loadedSgbdPath) {
                    await this.ediabas.loadSgbd(ecu);
                    this.loadedSgbdPath = cacheKey;
                }
                const paramList = normalizeParams(params);
                const rawResults = await this.ediabas.executeJob(jobName, {
                    params: paramList.length > 0 ? paramList : undefined,
                });
                this.lastResults = rawResults;
                this.currentState = "ready";
                return { sets: rawResults.map(convertResultSet) };
            }
            catch (error) {
                this.currentState = "error";
                this.lastError = {
                    code: error.code ?? -1,
                    text: error instanceof Error ? error.message : String(error),
                };
                throw error;
            }
        });
    }
    enqueue(task) {
        const next = this.queue.then(task, task);
        this.queue = next.then(() => undefined, () => undefined);
        return next;
    }
    resultSets() {
        return this.lastResults.length > 0 ? this.lastResults.length - 1 : 0;
    }
    resultText(name, set, _format) {
        const r = this.findResult(name, set);
        if (!r)
            return "";
        if (r.value instanceof Uint8Array)
            return `[${r.value.length} bytes]`;
        return String(r.value ?? "");
    }
    resultInt(name, set) {
        const r = this.findResult(name, set);
        if (!r)
            return 0;
        return typeof r.value === "number" ? Math.trunc(r.value) : 0;
    }
    resultReal(name, set) {
        const r = this.findResult(name, set);
        if (!r)
            return 0;
        return typeof r.value === "number" ? r.value : 0;
    }
    resultBinary(name, set) {
        const r = this.findResult(name, set);
        if (!r || !(r.value instanceof Uint8Array))
            return [];
        return Array.from(r.value);
    }
    resultFormat(name, set) {
        const r = this.findResult(name, set);
        if (!r)
            return undefined;
        return mapResultType(r.type);
    }
    state() {
        return this.currentState;
    }
    /**
     * Abort the currently-running job by forwarding `Ediabas.break()`
     * to the underlying interpreter (which throws `EDIABAS_BIP_0008`
     * at the next instruction boundary). Native EDIABAS `apiBreak`
     * semantics.
     *
     * Critical: this method **bypasses {@link enqueue}**. Routing it
     * through the same queue as `job()` would mean `break()` only
     * fires after the job it's trying to interrupt finishes, which
     * defeats the whole point. Calling `break()` while no job is in
     * flight is a no-op on the underlying `Ediabas` (the active-
     * interpreter pointer is null), so the timing race between "job
     * starting" and "break landing" is benign.
     */
    async break() {
        this.ediabas?.break();
        this.currentState = "break";
    }
    errorCode() {
        return this.lastError.code;
    }
    errorText() {
        return this.lastError.text;
    }
    findResult(name, set) {
        if (set < 0 || set >= this.lastResults.length)
            return undefined;
        return this.lastResults[set].find((r) => r.name.toUpperCase() === name.toUpperCase());
    }
}
/**
 * Coerce the public {@link IEdiabas.job} params union into the
 * `(string | Uint8Array)[]` shape `Ediabas.executeJob` expects. String
 * shorthand is split on `;` to preserve the apiJob convention; bare
 * `Uint8Array` becomes a single binary entry; arrays are passed
 * through untouched. The element type carries the channel: string →
 * indexed-string params (pari/pars), Uint8Array → binary payload
 * (pary/parb/parw/parl/parr).
 *
 * Exported for test reach; not part of the public surface. Callers
 * should drive `IEdiabas.job(...)` directly rather than reaching for
 * this helper.
 */
export function normalizeParams(params) {
    if (params === undefined)
        return [];
    if (typeof params === "string")
        return params.length > 0 ? params.split(";") : [];
    if (params instanceof Uint8Array)
        return [params];
    return params;
}
function mapResultType(type) {
    switch (type) {
        case "string": return "text";
        case "int": return "integer";
        case "real":
        case "float":
        case "double": return "real";
        case "bytes":
        case "binary": return "binary";
        case "long": return "long";
        case "bcd": return "text";
        default: return "text";
    }
}
function convertResultSet(results) {
    const set = {};
    for (const r of results) {
        const entry = {
            name: r.name,
            type: mapResultType(r.type),
            value: r.value instanceof Uint8Array ? Array.from(r.value) : r.value ?? "",
        };
        if (r.unit !== undefined)
            entry.unit = r.unit;
        if (r.comment !== undefined)
            entry.comment = r.comment;
        set[r.name] = entry;
    }
    return set;
}
//# sourceMappingURL=embedded-ediabas.js.map